import {
  Avatar,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Input,
  ListItem,
  Spinner,
  Badge,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { useDebounce } from 'use-debounce';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Trans } from '@lingui/react/macro';
import { ChevronRightIcon } from 'lucide-react';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { internalEnrolmentState } from '../../tenantState/InternalEnrolmentState';
import { ProviderUserRoleColorSchemes } from '@duke-hq/shared-constants/providers';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

type UsersList = User[];

function UserList({
  users,
  selectedMember,
}: {
  users: UsersList | undefined;
  selectedMember: User;
}) {
  const snap = useSnapshot(internalEnrolmentState);

  if (!users) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>Sorry, we couldn't load users. Please refresh or try again later.</Trans>
        </Text>
      </Box>
    );
  }

  if (users.length === 0) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>No users found.</Trans>
        </Text>
      </Box>
    );
  }

  return (
    <UnorderedList
      styleType="none"
      w="full"
      p="0"
      m="0"
      border="1px solid"
      borderRadius="8px"
      borderColor="border.emphasis"
      maxH="400px"
      overflowY="auto"
      sx={{
        'li:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'border.emphasis',
        },
        'li:first-of-type': {
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
        },
        'li:last-of-type': {
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
        },
      }}
    >
      {selectedMember?.id ? (
        <UserRow
          user={selectedMember}
          bg={snap.selectedUserAttributes.id === selectedMember.id ? 'bg.muted' : 'transparent'}
        />
      ) : (
        users?.map((user) => (
          <UserRow
            key={selectedMember.id}
            user={user}
            bg={
              selectedMember?.id || snap.selectedUserAttributes.id === user.id
                ? 'bg.muted'
                : 'transparent'
            }
          />
        ))
      )}
    </UnorderedList>
  );
}

function UserRow({ user, bg }: { user: User; bg: string }) {
  const handleClick = useCallback(() => {
    internalEnrolmentState.setSelectedUserAttributes({
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
    });
  }, [user]);

  return (
    <ListItem onClick={handleClick} p="8px 16px" bg={bg} cursor="pointer">
      <HStack justify="space-between" w="full">
        <Flex alignItems="center">
          <Avatar size="sm" mr="12px" name={`${user.first_name} ${user.last_name}`} />
          <Box>
            <Text fontWeight="medium">{`${user.first_name} ${user.last_name}`}</Text>
            <Text fontWeight="normal" color="fg.muted">
              {user.email}
            </Text>
          </Box>
        </Flex>
        <Badge
          borderRadius="full"
          colorScheme={ProviderUserRoleColorSchemes[user.role] ?? 'gray'}
          variant="muted"
          textTransform="none"
        >
          {user.role.replace(/_/g, ' ')}
        </Badge>
      </HStack>
    </ListItem>
  );
}

export function SelectParticipant({
  goToNextStep,
  selectedMember,
}: {
  goToNextStep: () => void;
  selectedMember: User;
}) {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 500);
  const snap = useSnapshot(internalEnrolmentState);
  const { data: usersList, isLoading } = trpcTenant.users.all.useQuery({
    search: debouncedSearch,
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const q = value.trim();
    setSearch(q);
  };

  useEffect(() => {
    if (selectedMember?.id) {
      internalEnrolmentState.setSelectedUserAttributes({
        id: selectedMember.id,
        first_name: selectedMember.first_name,
        last_name: selectedMember.last_name,
        email: selectedMember.email,
        role: selectedMember.role,
      });
    }
  }, [selectedMember]);

  return (
    <FormMainContainer>
      <FormContentContainer>
        {!selectedMember?.id && (
          <Input
            autoComplete="off"
            autoFocus
            fontSize="sm"
            minH="8"
            maxH="8"
            onChange={handleInputChange}
            placeholder="Search"
            size="sm"
            type="text"
            value={search}
            width="full"
          />
        )}
        {isLoading ? (
          <Center h="full" w="full">
            <Spinner color="border.accent" size="lg" />
          </Center>
        ) : (
          <UserList
            users={(usersList ?? []).map((user) => ({
              id: user.id,
              first_name: user.first_name ?? '',
              last_name: user.last_name ?? '',
              email: user.email ?? '',
              role: user.role,
            }))}
            selectedMember={selectedMember}
          />
        )}
      </FormContentContainer>
      <FormFooterContainer>
        <Button
          rightIcon={<ChevronRightIcon />}
          onClick={goToNextStep}
          isDisabled={!snap.selectedUserAttributes.id}
        >
          <Trans>Next</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}
